/* eslint-disable no-use-before-define */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import PropTypes from 'prop-types';
import secureLocalStorage from 'react-secure-storage';
import { Box } from '@mui/material';
import Swal from 'sweetalert2';
import Loader from 'ui-component/Loader';
import { useUsers } from 'hooks/api/users/useUsers';
import { useRole } from 'hooks/api/roles/context';
import { setKeycloakInstance } from 'utils/axios';
import { logger } from 'utils/environment/log';

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const POPUP_DURATION = 60 * 1000; // 60 seconds
const THROTTLE_DELAY = 1000; // 1 second throttle

const AuthGuard = ({ children }) => {
    const { getUserRoles } = useUsers();
    const { keycloak, initialized } = useKeycloak();
    const { setRoles } = useRole();
    const [rolesLoaded, setRolesLoaded] = useState(false);

    // Use refs for timers
    const timeoutRef = useRef(null);
    const popupTimeoutRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    const showTimeoutAlert = useCallback(() => {
        let timerInterval;
        Swal.fire({
            title: 'Session Timeout Warning',
            html: 'Your session will expire in <b>60</b> seconds.<br>Would you like to continue?',
            icon: 'warning',
            timer: POPUP_DURATION,
            timerProgressBar: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCancelButton: true,
            confirmButtonText: 'Continue Session',
            cancelButtonText: 'Logout',
            reverseButtons: true,
            didOpen: () => {
                timerInterval = setInterval(() => {
                    const content = Swal.getHtmlContainer();
                    if (content) {
                        const seconds = Math.ceil(Swal.getTimerLeft() / 1000);
                        const b = content.querySelector('b');
                        if (b) {
                            b.textContent = seconds;
                        }
                    }
                }, 1000);
            },
            willClose: () => {
                clearInterval(timerInterval);
            }
        }).then((result) => {
            if (result.isConfirmed) {
                // Continue session
                resetTimer();
            } else {
                // Logout on cancel or timer expiry
                keycloak.logout();
            }
        });
    }, [keycloak]);

    const resetTimer = useCallback(() => {
        const now = Date.now();
        if (now - lastActivityRef.current < THROTTLE_DELAY) {
            return;
        }
        lastActivityRef.current = now;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);

        timeoutRef.current = setTimeout(() => {
            showTimeoutAlert();
        }, INACTIVITY_TIMEOUT);
    }, [showTimeoutAlert]);

    useEffect(() => {
        if (!keycloak.authenticated) {
            return () => {};
        }

        const handleActivity = () => {
            resetTimer();
        };

        const activities = ['mousedown', 'keypress', 'scroll', 'touchstart'];

        activities.forEach((activity) => {
            document.addEventListener(activity, handleActivity, { passive: true });
        });

        // Initial timer setup
        resetTimer();

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);

            activities.forEach((activity) => {
                document.removeEventListener(activity, handleActivity);
            });
        };
    }, [keycloak.authenticated, resetTimer]);

    useEffect(() => {
        setKeycloakInstance(keycloak);

        const fetchAndStoreRoles = async () => {
            if (keycloak.authenticated) {
                try {
                    // Clear all existing data in secureLocalStorage when user logs in
                    secureLocalStorage.clear(); // Add this line to clear all existing data

                    const roles = await getUserRoles();
                    setRoles(roles);
                    secureLocalStorage.setItem('user_roles', JSON.stringify(roles));
                    secureLocalStorage.setItem('token', keycloak.token);
                } catch (error) {
                    secureLocalStorage.removeItem('user_roles');
                } finally {
                    setRolesLoaded(true);
                }
            }
        };

        if (keycloak.authenticated && !rolesLoaded) {
            fetchAndStoreRoles();
        }

        const refreshInterval = setInterval(() => {
            if (keycloak.authenticated) {
                keycloak
                    .updateToken(70)
                    .then((refreshed) => {
                        if (refreshed) {
                            logger.info('Token refreshed successfully');
                            secureLocalStorage.setItem('token', keycloak.token);
                        }
                    })
                    .catch((error) => {
                        logger.error('Token refresh failed:', error);
                        keycloak.logout();
                    });
            }
        }, 60000);

        return () => {
            clearInterval(refreshInterval);
        };
    }, [keycloak, keycloak.authenticated, getUserRoles, rolesLoaded, setRoles]);

    if (!initialized) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Loader />
            </Box>
        );
    }

    if (keycloak.authenticated) {
        const existingToken = secureLocalStorage.getItem('token');
        if (!existingToken) {
            secureLocalStorage.setItem('token', keycloak.token);
        }

        if (!rolesLoaded) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                    <Loader />
                </Box>
            );
        }

        return children;
    }

    keycloak.login();
    return null;
};

AuthGuard.propTypes = {
    children: PropTypes.node
};

export default AuthGuard;
